import { ref, get, set } from 'firebase/database';
import { database } from '../firebase/firebase';

/**
 * Every event an account can still be reached from: the ones it created, plus
 * the ones it has opened. Firebase keeps no "events I take part in" index, and
 * these two lists are the only ones a user is allowed to read for themselves.
 */
const collectEventIds = async (uid: string): Promise<string[]> => {
    const [owned, visited] = await Promise.all([
        get(ref(database, `userEvents/${uid}`)),
        get(ref(database, `userEventHistory/${uid}`)),
    ]);

    return Array.from(new Set([
        ...Object.keys(owned.val() || {}),
        ...Object.keys(visited.val() || {}),
    ]));
};

/**
 * Carry a new account name into the bills.
 *
 * Renaming the account used to touch `users/<uid>` alone, so every existing bill
 * kept the old name forever — reloading changed nothing, because that is simply
 * where the bills never looked.
 *
 * A name deliberately given for one event stays that event's name: only the
 * events still carrying the previous account name follow along. `forceEventId`
 * is the event the rename was made from, which always follows.
 *
 * Returns how many events were updated.
 */
export const syncAccountNameAcrossEvents = async (
    uid: string,
    newName: string,
    previousName: string,
    forceEventId?: string
): Promise<number> => {
    const eventIds = await collectEventIds(uid);
    if (forceEventId && !eventIds.includes(forceEventId)) {
        eventIds.push(forceEventId);
    }

    let updated = 0;

    await Promise.all(eventIds.map(async (eventId) => {
        try {
            const eventUserSnapshot = await get(ref(database, `eventUsers/${eventId}/${uid}`));
            if (!eventUserSnapshot.exists()) return; // not a participant of this event

            const currentName: string | undefined = eventUserSnapshot.val()?.displayName;
            const isForced = eventId === forceEventId;

            if (!isForced && currentName && previousName && currentName !== previousName) {
                return; // custom name for this event — leave it alone
            }
            if (currentName === newName) return;

            await set(ref(database, `eventUsers/${eventId}/${uid}/displayName`), newName);

            // `events/<id>/meta` is owner-writable only; for other people's events
            // the eventUsers entry above is what the bill reads.
            const ownerSnapshot = await get(ref(database, `events/${eventId}/meta/createdBy`));
            if (ownerSnapshot.val() === uid) {
                await set(ref(database, `events/${eventId}/meta/participants/${uid}`), newName);
            }

            updated += 1;
        } catch (error) {
            console.error(`Failed to sync display name for event ${eventId}:`, error);
        }
    }));

    return updated;
};
